import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ExternalLink, X } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "all", l: "All statuses" },
  { v: "confirmed", l: "Confirmed" },
  { v: "pending_payment", l: "Pending payment" },
  { v: "cancelled", l: "Cancelled" },
];

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "all";
  const airlineFilter = (params.get("airline") || "").toUpperCase();
  const typeFilter = (params.get("type") || "").toLowerCase();
  const navigate = useNavigate();
  const load = async () => { const { data } = await api.get("/admin/bookings"); setRows(data); };
  useEffect(() => { load(); }, []);

  const setStatus = (v) => {
    const np = new URLSearchParams(params);
    if (v === "all") np.delete("status"); else np.set("status", v);
    setParams(np);
  };

  const clearExtra = (k) => {
    const np = new URLSearchParams(params);
    np.delete(k);
    setParams(np);
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (status !== "all") out = out.filter((b) => b.status === status);
    if (typeFilter) out = out.filter((b) => (b.booking_type || "").toLowerCase() === typeFilter);
    if (airlineFilter) out = out.filter((b) => {
      const segs = ((b.item || {}).segments) || [];
      return segs.some((s) => (s.airline_code || "").toUpperCase() === airlineFilter);
    });
    return out;
  }, [rows, status, typeFilter, airlineFilter]);

  const sb = (s, kind = "booking") => {
    // Truthful palette: same colors as Transactions for consistency.
    const palettes = {
      booking: { confirmed: "bg-emerald-100 text-emerald-700",
                 pending_payment: "bg-amber-100 text-amber-700",
                 pending: "bg-amber-100 text-amber-700",
                 cancelled: "bg-red-100 text-red-700",
                 failed: "bg-red-100 text-red-700" },
      ticket:  { issued: "bg-emerald-100 text-emerald-700",
                 pending: "bg-amber-100 text-amber-700",
                 cancelled: "bg-red-100 text-red-700",
                 failed: "bg-red-100 text-red-700" },
    };
    const p = palettes[kind] || {};
    if (!s) return <Badge className="bg-slate-100 text-slate-700 border-0">—</Badge>;
    return <Badge className={`${p[s] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{String(s).replace(/_/g," ")}</Badge>;
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Bookings</h1>
          <p className="text-slate-500 mt-1">Click a row to open, service the ticket, void, refund, or add seats/bags/meals.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="bookings-status-filter" className="w-44 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {status !== "all" && (
            <Button data-testid="bookings-clear-filter" variant="ghost" size="sm" onClick={() => setStatus("all")} className="text-slate-500"><X className="w-3.5 h-3.5 mr-1" /> Clear</Button>
          )}
          {airlineFilter && (
            <Button data-testid="bookings-clear-airline" variant="outline" size="sm" onClick={() => clearExtra("airline")} className="rounded-full text-violet-700 border-violet-200">
              Airline: {airlineFilter} <X className="w-3 h-3 ml-1" />
            </Button>
          )}
          {typeFilter && (
            <Button data-testid="bookings-clear-type" variant="outline" size="sm" onClick={() => clearExtra("type")} className="rounded-full text-teal-700 border-teal-200 capitalize">
              Type: {typeFilter} <X className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-400">Showing {filtered.length} of {rows.length} bookings</div>

      <Card className="mt-4 overflow-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Txn ID</TableHead>
            <TableHead>PNR</TableHead><TableHead>Ticket #</TableHead><TableHead>Type</TableHead><TableHead>User</TableHead>
            <TableHead>Amount</TableHead><TableHead>Booking</TableHead><TableHead>Ticketing</TableHead>
            <TableHead>Created</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((b) => (
              <TableRow key={b.id} data-testid={`admin-booking-row-${b.id}`} className="cursor-pointer hover:bg-teal-50/40" onClick={() => navigate(`/admin/bookings/${b.id}`)}>
                <TableCell className="font-mono text-xs font-bold text-[var(--brand-primary-dark)]" data-testid={`booking-txn-id-${b.id}`}>{b.internal_ref || b.pnr_internal || "—"}</TableCell>
                <TableCell className="font-mono text-xs" title={b.pnr ? "GDS PNR" : "No PNR — booking failed at supplier"}>{b.pnr || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{b.ticket_no || "—"}</TableCell>
                <TableCell className="capitalize">{b.booking_type}</TableCell>
                <TableCell>{b.user_email}</TableCell>
                <TableCell className="font-semibold">
                  <div>${Number(b.total_amount).toFixed(2)}</div>
                  {b.original_currency && b.original_currency !== b.currency && (
                    <div className="text-[10px] text-slate-500 font-mono font-normal" title={`Sourced at ${b.original_total_amount} ${b.original_currency} · FX margin ${b.fx_margin_pct_applied ?? 0}%`}>
                      {Number(b.original_total_amount || 0).toFixed(0)} {b.original_currency} · +{Number(b.fx_margin_pct_applied || 0).toFixed(1)}%
                    </div>
                  )}
                </TableCell>
                <TableCell><span title={b.failure_reason || ""}>{sb(b.booking_status || b.status, "booking")}</span></TableCell>
                <TableCell><span title={b.failure_reason || ""}>{sb(b.ticketing_voucher_status, "ticket")}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/admin/bookings/${b.id}`); }}><ExternalLink className="w-3.5 h-3.5" /></Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-10">No bookings match this filter</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
